import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { setAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";

export default function RegisterPage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // If you were invited, paste the invite token to join the inviter’s team

  const [inviteToken, setInviteToken] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/register", {
        name,
        email,
        password,
        invite_token: inviteToken.trim() || null,
      });
      setAuth(res.data.token, res.data.user);
      toast({ title: "Account created" });
      navigate("/app", { replace: true });
    } catch (err) {
      toast({
        title: "Registration failed",
        description: err?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="register-page">
      <div className="mx-auto grid min-h-screen max-w-6xl grid-cols-1 gap-8 px-5 py-10 lg:grid-cols-2">
        <div className="flex flex-col justify-center">
          <h1
            className="text-4xl font-semibold tracking-tight text-slate-100 sm:text-5xl lg:text-6xl"
            data-testid="register-hero-title"
          >
            Build scripts that
            <br />
            never get stuck.
          </h1>
          <p
            className="mt-4 max-w-xl text-base text-slate-200/75"
            data-testid="register-hero-subtitle"
          >
            Create your account. Scripts and data are shared with your organization.
          </p>
          <div className="mt-6" data-testid="register-hero-links">
            <Link
              to="/login"
              className="text-sm text-slate-200/80 underline-offset-4 hover:underline"
              data-testid="register-go-to-login-link"
            >
              Back to login
            </Link>
          </div>
        </div>

        <div className="flex items-center justify-center lg:justify-end">
          <Card className="w-full max-w-md glass" data-testid="register-card">
            <CardHeader>
              <CardTitle className="text-slate-100" data-testid="register-card-title">
                Create account
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
                <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-sm text-slate-200/75" data-testid="register-role-note">
                  New sign-ups default to <span className="font-semibold text-slate-100">Sales Agent</span> and won’t see scripts until an Admin/Super Admin grants access.
                  If you received an invite, paste your invite token below.
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-slate-200/80" data-testid="register-name-label">
                    Name
                  </label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Alex"
                    className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                    data-testid="register-name-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-slate-200/80" data-testid="register-email-label">
                    Email
                  </label>
                  <Input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    type="email"
                    autoComplete="email"
                    className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                    data-testid="register-email-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-slate-200/80" data-testid="register-password-label">
                    Password
                  </label>
                  <Input
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    type="password"
                    autoComplete="new-password"
                    className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                    data-testid="register-password-input"
                    required
                  />
                </div>

                <div className="space-y-2" data-testid="register-invite-token-field">
                  <label className="text-sm text-slate-200/80" data-testid="register-invite-token-label">
                    Invite token (optional)
                  </label>
                  <Input
                    value={inviteToken}
                    onChange={(e) => setInviteToken(e.target.value)}
                    placeholder="Paste token from your invite link"
                    className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                    data-testid="register-invite-token-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500"
                  data-testid="register-submit-button"
                >
                  {loading ? "Creating…" : "Create account"}
                </Button>

                <div className="text-sm text-slate-200/70" data-testid="register-login-hint">
                  Already have an account?{" "}
                  <Link
                    to="/login"
                    className="text-slate-100 underline-offset-4 hover:underline"
                    data-testid="register-login-link"
                  >
                    Login
                  </Link>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
